import { useEffect, useRef, useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type SortingState,
  type Updater,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ScanSearch } from 'lucide-react';
import { columnWidthStyle, fixedTableClassName, fixedTableStyle } from '@/components/data-table/table-layout';
import { type RowEntity, type TableSettings, useTableStore } from '@/hooks/data/datatable/useTableStore';
import { DataTableListLoadingSkeleton } from '@/components/data-table/data-table-list-loading-skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { type PaginationState, defaultPaginationIndex, pageSizeOptions } from '@/types/pagination';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { EmptyLayout } from '@/components/empty-layout';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { type EntityType } from '@/types/entity';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

interface DataTableProps<TData, TValue> {
  columns: Array<ColumnDef<TData, TValue>>;
  data: Array<TData>;
  options?: Array<{ value: string; label: string; initials: string }>;
  pagination?: PaginationState;
  withPagination?: boolean;
  rowCount?: number;
  entity: EntityType;
  setSelectedEntity: any;
  setSelectedEntityType: any;
  tableContainer?: string;
  intrinsicWidth?: boolean;
  tableEntity: EntityType;
  tableSettings: Partial<TableSettings>;
  isLoading?: boolean;
  /** Entity name for the "No results" message when it differs from tableEntity (e.g. the Collaborators tab is User-backed). */
  noResultsEntity?: string;
}

export function DataTable<TData, TValue>({
  tableEntity,
  tableSettings,
  columns,
  data,
  // options,
  pagination,
  withPagination = true,
  rowCount,
  entity,
  setSelectedEntity,
  setSelectedEntityType,
  tableContainer,
  intrinsicWidth = false,
  isLoading = false,
  noResultsEntity,
}: DataTableProps<TData, TValue>) {
  const settings = useTableStore((s) => s.settings[tableEntity] ?? tableSettings);
  const setSettings = useTableStore((s) => s.setSettings);
  const scrollRef = useRef<HTMLDivElement>(null);

  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const setShowInspector = useUserSettingsStore((s) => s.setShowInspector);
  const { isManagePage } = useCurrentPage();
  const tEmptyStates = useTranslations('emptyStates');

  const setDataTablePagination = (updater: Updater<PaginationState>) => {
    if (typeof updater !== 'function') {
      return;
    }
    const oldPageInfo = table.getState().pagination;
    const newPageInfo = updater(oldPageInfo);
    if (oldPageInfo.pageSize !== newPageInfo.pageSize) {
      newPageInfo.pageIndex = defaultPaginationIndex;
    }
    setSettings(tableEntity, { pageSize: newPageInfo.pageSize, pageIndex: newPageInfo.pageIndex });
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      rowSelection,
      columnFilters,
      pagination,
    },
    rowCount: rowCount,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onPaginationChange: setDataTablePagination,
    manualPagination: true, //we're doing manual "server-side" pagination
    // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
    debugTable: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: (updater: Updater<VisibilityState>) =>
      setSettings(tableEntity, {
        columnVisibility: typeof updater === 'function' ? updater(settings.columnVisibility!) : updater,
      }),
    onColumnOrderChange: (updater: Updater<ColumnOrderState>) =>
      setSettings(tableEntity, {
        columnOrder: typeof updater === 'function' ? updater(settings.columnOrder!) : updater,
      }),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (row) => (row as { id: string }).id,
  });

  useEffect(() => {
    if (isLoading) {
      return;
    }
    const rowId = Object.keys(rowSelection)[0];
    const row = rowId ? (table.getCoreRowModel().rowsById[rowId] as RowEntity | undefined) : undefined;
    if (rowId && !row) {
      // The selected row is not in the current data (e.g. a server-side filter dropped it).
      // Keep the previous selection — it reappears selected when the filter clears.
      // Looked up via rowsById because table.getRow throws on unknown ids.
      return;
    }
    setSelectedEntity(row ?? null);
    setSelectedEntityType(entity);
    setShowInspector(true);
  }, [rowSelection, isLoading]);

  const pageIndex = table.getState().pagination?.pageIndex ?? defaultPaginationIndex;

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }, [pageIndex]);

  const hasRows = Boolean(table.getRowModel().rows?.length);

  if (isLoading) {
    return <DataTableListLoadingSkeleton />;
  }

  return (
    <>
      <div ref={scrollRef} className={cn('relative w-full grow overflow-auto bg-ui-container', tableContainer)}>
        {hasRows ? (
          <Table
            className={intrinsicWidth ? 'w-max! min-w-full table-auto' : fixedTableClassName}
            style={intrinsicWidth ? undefined : fixedTableStyle(table)}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} data-cy="table-header-row">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn(header.column.columnDef.meta?.headerClassName)}
                      style={intrinsicWidth ? undefined : columnWidthStyle(header.column)}
                      data-cy={`table-header-${header.column.id}`}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  onClick={(event) => {
                    if (row.getIsSelected()) {
                      return;
                    }
                    row.getToggleSelectedHandler()(event);
                  }}
                  key={row.id}
                  {...(row.getIsSelected() && { 'data-state': 'selected' })}
                  className={cn({ 'bg-ui-base': isManagePage })}
                  data-cy={`table-row-${row.index}`}
                >
                  {row
                    .getVisibleCells()
                    .map((cell: { id: string; column: { columnDef: any }; getContext: () => any }) => (
                      <TableCell key={cell.id} className={cell.column.columnDef.meta?.cellClassName}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyLayout
            icon={ScanSearch}
            title={tEmptyStates('noResults.title')}
            message={tEmptyStates('noResults.description', { entity: noResultsEntity ?? tableEntity })}
          />
        )}
      </div>
      {withPagination && hasRows && (
        <DataTablePagination table={table} className="px-3 py-2" pageSizeOptions={pageSizeOptions} />
      )}
    </>
  );
}
